import { useEffect, useMemo } from 'react';

import axios from 'axios';

import { format, parseISO } from 'date-fns';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import CurrencyInput from 'react-currency-input-field';

import {
  CheckOutline,
  CloseCircleOutline,
  DEFAULT_DATE_FORMAT,
  DropdownInput,
  DropdownOption,
  getError,
  individualFullName,
  useToast,
} from '@aslanpay/portal-common-lib';

import { Payday, PayFrequency } from 'app/constants/payCalendar';

import useFetchAllPayCycles from 'app/hooks/useFetchAllPayCycles';
import useFetchPayCalendars from 'app/hooks/useFetchPayCalendars';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useUpdateIndividual from 'app/hooks/useUpdateIndividual';

import { derivePaySchedule } from 'app/utils/payCalendars';
import { reassignPayCalendarSchema } from 'app/utils/schemas';
import { parseCurrencyIntoFloat } from 'app/utils/strings';

import { ReassignPayCalendarFormFields, ReassignPayCalendarModalProps } from './types';

const formatDate = (date?: string) => (date ? format(parseISO(date), DEFAULT_DATE_FORMAT) : '—');

function ReassignPayCalendarModal({ isOpen, onClose, individual }: ReassignPayCalendarModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const defaultEmploymentType: 'SALARIED' | 'NON_SALARIED' = individual?.salaried
    ? 'SALARIED'
    : 'NON_SALARIED';

  const employmentTypeOptions: DropdownOption[] = [
    { value: 'SALARIED', text: 'Salaried' },
    { value: 'NON_SALARIED', text: 'Non-salaried' },
  ];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<ReassignPayCalendarFormFields>({
    mode: 'onChange',
    defaultValues: {
      targetPayCalendarId: individual?.payCalendarId ?? '',
      employmentType: defaultEmploymentType,
    },
    resolver: yupResolver(reassignPayCalendarSchema),
  });

  // Prefer the individual's own last-known salary, then their most recent salary history entry.
  // Used to seed the salary field both on open (below) and whenever the admin toggles employment
  // type afterwards (further down, where the target calendar's default is also considered).
  const mostRecentSalaryHistoryEntry = individual?.salaryHistory?.length
    ? [...individual.salaryHistory].sort((a, b) => b.appliesDate.localeCompare(a.appliesDate))[0]
    : undefined;
  const initialSalary =
    individual?.annualGrossSalary ?? mostRecentSalaryHistoryEntry?.annualGrossSalary;

  useEffect(() => {
    if (isOpen) {
      reset({
        // Preselecting the worker's own calendar means the form starts at their current state,
        // like a normal edit form — Confirm stays disabled (via isNoActualChange, below) until the
        // admin actually changes the calendar and/or employment type.
        targetPayCalendarId: individual?.payCalendarId ?? '',
        employmentType: defaultEmploymentType,
        // reset() replaces every field not listed here with an empty value, so annualGrossSalary
        // must be seeded explicitly too — otherwise an already-salaried worker's saved salary
        // gets wiped on open instead of prefilled (the toggle effect below won't re-run to fix it
        // since employmentType's value hasn't actually changed in that case).
        annualGrossSalary:
          defaultEmploymentType === 'SALARIED' && initialSalary ? initialSalary.toString() : '',
      });
    }
    // defaultEmploymentType/initialSalary intentionally excluded — both derive from `individual`,
    // which is already stable for the lifetime of a single open modal; re-including them would
    // re-run this effect (and reset the form) on every individual refetch while the modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reset]);

  const targetPayCalendarId = watch('targetPayCalendarId');
  const employmentType = watch('employmentType');
  const annualGrossSalary = watch('annualGrossSalary');

  const { data: payCalendarsPage } = useFetchPayCalendars(
    {
      companyId: individual?.companyId ? [individual.companyId] : undefined,
      status: ['ACTIVE'],
      page: 0,
      size: 999999,
    },
    !!individual?.companyId && isOpen
  );

  // The current calendar is a valid selection too — it's how an admin changes employment type
  // only, without also moving the worker to a different calendar.
  const payCalendarOptions: DropdownOption[] = (payCalendarsPage?.list || []).map(
    (payCalendar) => ({
      value: payCalendar.id,
      text:
        payCalendar.id === individual?.payCalendarId
          ? `${payCalendar.name} (current)`
          : payCalendar.name,
    })
  );

  const { data: currentPayCalendar } = useFetchPayCalendar(individual?.payCalendarId);
  const { data: currentPayCycles } = useFetchAllPayCycles(individual?.payCalendarId);
  const currentSchedule = useMemo(
    () => derivePaySchedule(currentPayCycles?.list),
    [currentPayCycles]
  );

  const { data: targetPayCalendar } = useFetchPayCalendar(targetPayCalendarId || undefined);
  const { data: targetPayCycles } = useFetchAllPayCycles(targetPayCalendarId || undefined);
  const targetSchedule = useMemo(() => derivePaySchedule(targetPayCycles?.list), [targetPayCycles]);

  const becomingSalaried = employmentType === 'SALARIED';

  const enteredSalary = annualGrossSalary ? parseCurrencyIntoFloat(annualGrossSalary) : NaN;
  const isEnteredSalaryValid = !Number.isNaN(enteredSalary) && enteredSalary > 0;
  const isSalaryInputSatisfied = !becomingSalaried || isEnteredSalaryValid;

  // Gates the warning banner. A salary typed into the field satisfies the backend's salary-basis
  // rule just as stored data does (it is sent with the PUT), so it must clear the banner too —
  // otherwise the banner tells the admin to add a salary while Confirm, which gates on
  // isSalaryInputSatisfied above, is already enabled. Declared after the entered-salary checks for
  // that reason.
  const requiresSalaryInput =
    !!targetPayCalendarId &&
    becomingSalaried &&
    !isEnteredSalaryValid &&
    !individual?.salaryHistory?.length &&
    !targetPayCalendar?.defaultAnnualGrossSalary;

  // Falls back further to the target calendar's default once one's picked — initialSalary
  // (computed above) only covers the individual's own data, needed before a target exists.
  const prefillSalary = initialSalary ?? targetPayCalendar?.defaultAnnualGrossSalary;

  // The current calendar is a selectable target (for an employment-type-only change), but picking
  // it while leaving employment type untouched is a no-op submission — nothing on the worker would
  // actually change. Block Confirm rather than let the admin fire a pointless PUT.
  const isNoActualChange =
    !!targetPayCalendarId &&
    targetPayCalendarId === individual?.payCalendarId &&
    becomingSalaried === !!individual?.salaried;

  useEffect(() => {
    // No shouldValidate here: this also runs on mount (before a target calendar is picked), and
    // forcing a full yup-schema validation that early would prematurely flag targetPayCalendarId
    // as invalid. The salary field's own rule is just notRequired(), so it doesn't need it anyway.
    setValue(
      'annualGrossSalary',
      becomingSalaried && prefillSalary ? prefillSalary.toString() : ''
    );
    // Re-run on every employment-type switch AND every target-calendar switch, not just once:
    // toggling back to salaried must always restore the worker's saved salary, and picking a
    // different target must apply that calendar's default salary — in both cases discarding any
    // value the admin had typed against the previous selection rather than submitting a stale one.
    // prefillSalary is a dependency in its own right because targetPayCalendar is fetched
    // asynchronously: keying on the id alone would re-run before the target's
    // defaultAnnualGrossSalary has arrived and miss it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employmentType, targetPayCalendarId, prefillSalary]);

  const { mutate: updateIndividual, isLoading: isSubmitting } = useUpdateIndividual({
    onSuccess: () => {
      const workerName = individual ? individualFullName(individual) : 'the worker';
      const calendarChanged = targetPayCalendarId !== individual?.payCalendarId;
      const targetName = currentPayCalendar?.name
        ? targetPayCalendar?.name ?? currentPayCalendar.name
        : '';

      // A same-calendar submission is a pure employment-type change, so the copy shouldn't claim
      // the worker was moved to a new calendar.
      const description = calendarChanged
        ? `Reassigned ${workerName} from ${
            currentPayCalendar?.name || 'their previous calendar'
          } to ${targetName} on ${format(new Date(), DEFAULT_DATE_FORMAT)}.`
        : `Updated ${workerName}’s employment type on ${format(new Date(), DEFAULT_DATE_FORMAT)}.`;

      toast({
        position: 'top-right',
        status: 'success',
        title: calendarChanged ? 'Pay calendar reassigned' : 'Employment type updated',
        description,
      });

      queryClient.invalidateQueries({ queryKey: ['fetchIndividual'] });
      queryClient.invalidateQueries({ queryKey: ['fetchIndividual', individual?.id] });
      queryClient.invalidateQueries({ queryKey: ['fetchEmployeesPage'] });

      onClose();
    },
    onError: (error) => {
      const { codeDescription, message } = getError(error);

      const copyByCode: Partial<Record<string, string>> = {
        ERE_TARGET_NOT_FOUND:
          'The selected pay calendar no longer exists. Please pick another one.',
        ERE_TARGET_NOT_ACTIVE:
          'The selected pay calendar is no longer active. Please pick another one.',
        ERE_TARGET_DIFFERENT_COMPANY:
          'The selected pay calendar does not belong to this worker’s company.',
        ERE_INDIVIDUAL_TERMINATED: 'This worker has left and can no longer be reassigned.',
        ERE_NOT_AN_EMPLOYEE:
          'Only employees are assigned a pay calendar, so this individual cannot be reassigned.',
        ERE_SOURCE_NOT_FOUND: 'This worker’s current pay calendar no longer exists.',
        ERE_SOURCE_DIFFERENT_COMPANY:
          'This worker’s current pay calendar does not belong to their company.',
        ERE_SOURCE_CYCLE_NOT_FOUND:
          'This worker’s current pay calendar has no open pay cycle to settle against. Please contact engineering.',
        ERE_SALARY_INFO_REQUIRED:
          'This worker has no salary info on file and the target calendar has no default salary. Add salary info before reassigning.',
        ERE_PAY_CALENDAR_CHANGE_FORBIDDEN:
          'You do not have permission to change this worker’s pay calendar.',
        ERE_SALARIED_CHANGE_FORBIDDEN:
          'You do not have permission to change this worker’s employment type.',
        CONFLICT: 'Another change is in flight for this worker, please retry.',
      };

      const settlementStepCopy: Partial<Record<string, string>> = {
        CANCEL_DR: 'cancelling pending deduction requests',
        FORCE_SETTLE_DR: 'settling available deduction requests',
        MOVE_TO_CARRYOVER: 'moving the residual salary to carryover',
      };

      let description = copyByCode[codeDescription || ''] || message || 'Something went wrong.';

      if (codeDescription === 'ERE_SETTLEMENT_FAILED' && axios.isAxiosError(error)) {
        const step = error.response?.data?.subErrors?.[0]?.field;
        const stepCopy = step ? settlementStepCopy[step] : undefined;
        description = stepCopy
          ? `Reassignment failed while ${stepCopy}. Please retry or contact engineering.`
          : 'Reassignment failed during settlement. Please retry or contact engineering.';
      }

      toast({
        position: 'top-right',
        status: 'error',
        title: 'Unable to reassign pay calendar',
        description,
      });
    },
  });

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleConfirm = (data: ReassignPayCalendarFormFields) => {
    if (!individual || !isSalaryInputSatisfied) {
      return;
    }

    updateIndividual({
      ...individual,
      id: individual.id,
      payCalendarId: data.targetPayCalendarId,
      salaried: data.employmentType === 'SALARIED',
      ...(becomingSalaried ? { annualGrossSalary: enteredSalary } : {}),
    });
  };

  const comparisonRows: Array<{
    label: string;
    current: string | undefined;
    target: string | undefined;
  }> = [
    { label: 'Name', current: currentPayCalendar?.name, target: targetPayCalendar?.name },
    {
      label: 'Pay frequency',
      current: currentPayCalendar && PayFrequency[currentPayCalendar.payFrequency],
      target: targetPayCalendar && PayFrequency[targetPayCalendar.payFrequency],
    },
    {
      label: 'Payday',
      current: currentPayCalendar?.payday ? Payday[currentPayCalendar.payday] : undefined,
      target: targetPayCalendar?.payday ? Payday[targetPayCalendar.payday] : undefined,
    },
    {
      label: 'Next cutoff',
      current: formatDate(currentSchedule.nextCutoffDate),
      target: targetPayCalendarId ? formatDate(targetSchedule.nextCutoffDate) : undefined,
    },
    {
      label: 'ODP allowance',
      current: currentPayCalendar ? `${currentPayCalendar.odpAllowancePercentage}%` : undefined,
      target: targetPayCalendar ? `${targetPayCalendar.odpAllowancePercentage}%` : undefined,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={!isSubmitting}
      closeOnOverlayClick={!isSubmitting}
      onClose={handleClose}
      size="3xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="calc(100vh - 32px)">
        <ModalHeader>
          <HStack>
            <Text as="h6">
              Reassign pay calendar{individual ? ` – ${individualFullName(individual)}` : ''}
            </Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close reassign pay calendar modal"
              onClick={handleClose}
              isDisabled={isSubmitting}
            />
          </HStack>
        </ModalHeader>
        {/*
          Reserves the scrollbar gutter up front. Without this, fields that appear/disappear (e.g.
          the salary input on switching to Salaried) can push content past maxH, toggling the
          vertical scrollbar on and off — which shrinks/restores the content width each time and
          reads as the modal briefly widening.
        */}
        <ModalBody sx={{ scrollbarGutter: 'stable' }}>
          <form id="reassign-pay-calendar-form" onSubmit={handleSubmit(handleConfirm)} noValidate>
            <VStack spacing="16px" align="stretch">
              <FormControl isInvalid={!!errors?.targetPayCalendarId} isRequired>
                <FormLabel>Target pay calendar</FormLabel>
                <Controller
                  control={control}
                  name="targetPayCalendarId"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DropdownInput
                      placeholder="Select the target pay calendar"
                      defaultValue={payCalendarOptions.find((option) => option.value === value)}
                      options={payCalendarOptions}
                      onChange={(option) => onChange(option != null ? String(option) : '')}
                      onBlur={onBlur}
                      isSearchable
                    />
                  )}
                />
                <FormErrorMessage>{errors?.targetPayCalendarId?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors?.employmentType} isRequired>
                <FormLabel>New employment type</FormLabel>
                <Controller
                  control={control}
                  name="employmentType"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DropdownInput
                      placeholder="Select employment type"
                      defaultValue={employmentTypeOptions.find((option) => option.value === value)}
                      options={employmentTypeOptions}
                      onChange={(option) =>
                        onChange(option != null ? String(option) : defaultEmploymentType)
                      }
                      onBlur={onBlur}
                    />
                  )}
                />
                <FormErrorMessage>{errors?.employmentType?.message}</FormErrorMessage>
              </FormControl>

              {becomingSalaried && (
                <FormControl isInvalid={!!annualGrossSalary && !isEnteredSalaryValid} isRequired>
                  <FormLabel>Annual gross salary</FormLabel>
                  <Controller
                    control={control}
                    name="annualGrossSalary"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <InputGroup>
                        <Input
                          as={CurrencyInput}
                          value={value || ''}
                          onValueChange={(newValue) => onChange(newValue || '')}
                          onBlur={onBlur}
                          prefix="£"
                          decimalSeparator="."
                          groupSeparator=","
                          decimalScale={2}
                          type="text"
                          placeholder="Enter annual gross salary. E.g. £24,000.00"
                          pr="86px"
                        />
                        <InputRightElement w="86px">
                          <HStack spacing="18px" height="32px">
                            <Divider borderColor="brand.mediumGray" orientation="vertical" />
                            <Text as="h6" color="brand.darkGray" textStyle="h6Medium">
                              GBP
                            </Text>
                          </HStack>
                        </InputRightElement>
                      </InputGroup>
                    )}
                  />
                  <FormErrorMessage>Enter a valid salary</FormErrorMessage>
                </FormControl>
              )}

              {requiresSalaryInput && (
                <Alert status="warning" borderRadius="md" data-testid="salary-info-warning">
                  <AlertIcon />
                  <AlertDescription>
                    This worker has no salary info on file and the target calendar has no default
                    salary. Add salary info before reassigning.
                  </AlertDescription>
                </Alert>
              )}

              <Divider />

              <Box>
                <Text textStyle="smallBold" mb="8px">
                  Comparison
                </Text>
                <TableContainer>
                  <Table size="md" data-testid="pay-calendar-comparison">
                    <Thead>
                      <Tr>
                        <Th />
                        <Th>Current</Th>
                        <Th>Target</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {comparisonRows.map((row) => (
                        <Tr key={row.label}>
                          <Td>{row.label}</Td>
                          <Td>{row.current || '—'}</Td>
                          <Td>{row.target || ''}</Td>
                        </Tr>
                      ))}
                      <Tr>
                        <Td>Employment type</Td>
                        <Td>{individual?.salaried ? 'Salaried' : 'Non-salaried'}</Td>
                        <Td>
                          {employmentTypeOptions.find((option) => option.value === employmentType)
                            ?.text || ''}
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </VStack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Spacer />
          <Button variant="outline" size="sm" onClick={handleClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            form="reassign-pay-calendar-form"
            type="submit"
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isSubmitting}
            isDisabled={!isValid || !isSalaryInputSatisfied || isNoActualChange}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ReassignPayCalendarModal;
